/** Вкладка «Без цены»: продажи периода без себестоимости (`GET /missing`) — нет цены у
 *  характеристики (можно добавить здесь) или оффер не сопоставлен / неоднозначен в 1С (исправлять в 1С).
 *  С 22.10.2026 — и опт (`?src=all|mp|opt`, по умолчанию все): товар МП и опта с одной характеристикой —
 *  одна строка, каналы и остатки заказов опта (виды не складываются) — в строке. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { useUrlParam } from "@/lib/url-state";

import {
  downloadMissingXlsx,
  getMissing,
  type MissingItem,
  type MissingList,
  type MissingOptBucket,
  type MissingSourceFilter,
} from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { saveBlob } from "@/lib/download";
import { int } from "../ozon-analytics/format";
import { qty as qtyText } from "../wholesale/calc";
import { dateRu, dateTimeMsk, money, MISSING_HINT, MISSING_STATUS_STYLE, monthStart, todayIso } from "./format";
import { apiErrorText } from "./errors";
import { AddPriceDialog, type PricePreset } from "./PriceDialogs";
import { KEY } from "./queries";
import { Badge, CharacteristicLine, ErrorBox, Loading } from "./ui";

const SOURCE_OPTIONS: { value: MissingSourceFilter; label: string }[] = [
  { value: "mp", label: "МП" },
  { value: "opt", label: "Опт" },
  { value: "all", label: "Все" },
];

const asSource = (v: string): MissingSourceFilter => (v === "mp" || v === "opt" ? v : "all");

const amber =
  "rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300";

/** Период по умолчанию: с `cogs_from` (известен из ответа) или с начала месяца — по сегодня.
 *  Пока человек не трогал «с», первая выдача (с начала месяца) переключается на `cogs_from`. */
export function MissingTab(): React.ReactElement {
  const today = todayIso();
  // период живёт в адресе (`?from=&to=`): по ссылке из аналитики вкладка открывается на нужном периоде
  const [fromParam, setFromParam] = useUrlParam("from");
  const [toParam, setToParam] = useUrlParam("to");
  // кабинет (юрлицо) из аналитики: `?legal=` — только его продажи; сбрасывается кнопкой
  const [legal, setLegal] = useUrlParam("legal");
  // площадка плашки (`?mp=ozon|wb`): кабинет проверяется по витрине этой площадки; нет — все
  const [mp] = useUrlParam("mp");
  // канал продаж: МП / опт / все (по умолчанию)
  const [srcParam, setSrc] = useUrlParam("src");
  // кабинет и площадка — фильтры МП: опт с ними не смотрится
  const mpFiltered = !!legal || !!mp;
  // `?src=opt` вместе с кабинетом / площадкой бэкенд отвергает (422) — показываем «Все»: МП-часть
  // с пояснением «Опт не показан», а не ошибку при выключенной кнопке «Опт»
  const source = mpFiltered && asSource(srcParam) === "opt" ? "all" : asSource(srcParam);
  const to = toParam || today;
  // «с» по умолчанию — начало месяца, но не позже заданного «по»
  const from = fromParam || (to < monthStart(today) ? monthStart(to) : monthStart(today));
  // «с» из адреса — выбор человека (ссылка), `cogs_from` его не двигает
  const [fromTouched, setFromTouched] = useState(fromParam !== "");
  const setFrom = setFromParam;
  const setTo = setToParam;
  const [adding, setAdding] = useState<PricePreset | null>(null);
  const valid = !!from && !!to && from <= to;
  const q = useQuery({
    queryKey: KEY.missing(from, to, legal, mp, source),
    queryFn: () => getMissing(from, to, legal || null, mp || null, source),
    enabled: valid,
    retry: false,
  });
  const cogsFrom = q.data?.cogs_from ?? null;
  const optForbidden = q.data?.opt.status === "forbidden";
  useEffect(() => {
    // «с» не должно оказаться позже «по»: тогда оставляем как есть и подсказываем (ниже)
    if (!fromTouched && cogsFrom && cogsFrom !== from && cogsFrom <= to) setFrom(cogsFrom);
  }, [cogsFrom, fromTouched, from, to, setFrom]);
  const cogsAfterTo = !fromTouched && !!cogsFrom && cogsFrom > to;

  const items = q.data?.items ?? [];
  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  const totalRev = items.reduce((s, i) => s + i.revenue, 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-sm">
          <span className="block font-medium">Продажи с</span>
          <Input
            type="date"
            className="w-44"
            aria-label="Период с"
            value={from}
            onChange={(e) => {
              setFromTouched(true);
              setFrom(e.target.value);
            }}
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="block font-medium">по</span>
          <Input type="date" className="w-44" aria-label="Период по" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <div className="space-y-1 text-sm">
          <span className="block font-medium">Продажи</span>
          <div className="flex gap-1" role="group" aria-label="Канал продаж">
            {SOURCE_OPTIONS.map((o) => {
              const off = o.value === "opt" && (mpFiltered || optForbidden);
              return (
                <Button
                  key={o.value}
                  size="sm"
                  variant={source === o.value ? "default" : "outline"}
                  className="h-9"
                  aria-pressed={source === o.value}
                  disabled={off}
                  title={
                    off
                      ? mpFiltered
                        ? "Кабинет / площадка — фильтр МП: опт с ним не показывается"
                        : "Нет права «Аналитика ОПТ»"
                      : undefined
                  }
                  onClick={() => setSrc(o.value === "all" ? "" : o.value)}
                >
                  {o.label}
                </Button>
              );
            })}
          </div>
        </div>
        {legal && (
          <span className="flex items-center gap-2 pb-1.5 text-sm" data-testid="missing-legal">
            Кабинет: <b>{legal}</b>
            <Button size="sm" variant="outline" className="h-7" onClick={() => setLegal("")}>
              Все кабинеты
            </Button>
          </span>
        )}
        {q.data && (
          <MissingXlsxButton
            from={from}
            to={to}
            legal={legal || null}
            mp={mp || null}
            source={source}
            disabled={!items.some((i) => i.status === "no_price")}
          />
        )}
        {q.data && (
          <span className="pb-2 text-sm text-muted-foreground">
            себестоимость считается с {cogsFrom ? dateRu(cogsFrom) : "— (дата начала не задана)"}
            <span data-testid="missing-dwh-time"> · Данные DWH на {dateTimeMsk(q.data.dwh.built_at)}</span>
          </span>
        )}
      </div>
      {q.data?.stale && (
        <div role="status" className={amber} data-testid="missing-stale">
          Цены менялись в портале после загрузки в DWH (последнее изменение — {dateTimeMsk(q.data.portal_changed_at)},
          DWH взял цены — {q.data.dwh.prices_exported_at ? dateTimeMsk(q.data.dwh.prices_exported_at) : "ещё не брал"}).
          Список и маржа обновятся после ежедневной загрузки: цены — 08:30, сборка — 10:30 МСК.
        </div>
      )}
      {cogsAfterTo && (
        <p role="status" className="text-sm text-muted-foreground">
          Себестоимость считается с {dateRu(cogsFrom!)} — позже «по» ({dateRu(to)}); «с» оставлено {dateRu(from)}.
        </p>
      )}
      {q.data && <OptNote data={q.data} />}
      {!valid && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          Укажите период: «с» не позже «по».
        </p>
      )}
      {q.isLoading && <Loading text="Ищу продажи без цены…" />}
      {q.isError && <ErrorBox error={q.error} what="Список «без цены» не загрузился" />}
      {q.data && items.length === 0 && (
        <p className="rounded-md border px-4 py-6 text-sm text-muted-foreground" data-testid="missing-empty">
          За {dateRu(from)}–{dateRu(to)} все продажи с себестоимостью (по данным DWH на{" "}
          {dateTimeMsk(q.data.dwh.built_at)}).
        </p>
      )}
      {q.data && items.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm" data-testid="missing-table">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-2 font-medium">Статус</th>
                <th className="px-2 py-2 font-medium">Канал</th>
                <th className="px-2 py-2 font-medium">Артикул 1С / название</th>
                <th className="px-2 py-2 font-medium">Офферы</th>
                <th className="px-2 py-2 text-right font-medium">Штук</th>
                <th className="px-2 py-2 text-right font-medium">Выручка</th>
                <th className="px-2 py-2" />
              </tr>
              <tr className="border-t text-foreground">
                <td className="px-2 py-1 text-xs" colSpan={4}>
                  Итого строк: {items.length} · штуки и выручка — продано (МП + опт отгружено)
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{qtyText(totalQty)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{money(totalRev)}</td>
                <td />
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <MissingTr
                  key={`${it.status}:${it.nomenclature_ref}:${it.characteristic_ref}:${i}`}
                  it={it}
                  onAdd={() =>
                    setAdding({
                      product: {
                        nomenclature_ref: it.nomenclature_ref!,
                        characteristic_ref: it.characteristic_ref!,
                        code_1c: it.code_1c,
                        article_1c: it.article_1c,
                        name_1c: it.name_1c,
                        characteristic_name: it.characteristic_name,
                        offers: it.offers,
                      },
                      valid_from: cogsFrom,
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddPriceDialog preset={adding} onClose={() => setAdding(null)} />}
    </div>
  );
}

/** «Скачать xlsx»: товары без цены за период — шаблоном загрузки (заполнить цену, источник, причину
 *  и загрузить «Загрузить xlsx»). */
function MissingXlsxButton({
  from,
  to,
  legal,
  mp,
  source,
  disabled,
}: {
  from: string;
  to: string;
  legal: string | null;
  mp: string | null;
  source: MissingSourceFilter;
  disabled: boolean;
}): React.ReactElement {
  const dl = useMutation({
    mutationFn: () => downloadMissingXlsx(from, to, legal, mp, source),
    onSuccess: (blob) => saveBlob(blob, `no-price-${from}-${to}.xlsx`),
    onError: (e) => toast.error(`Файл не скачался: ${apiErrorText(e)}`),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="mb-0.5"
      onClick={() => dl.mutate()}
      disabled={disabled || dl.isPending}
      title="Товары без цены — шаблоном загрузки: впишите цену, источник и причину, удалите строки, которые не заполняете, и загрузите «Загрузить xlsx»"
    >
      <Download /> {dl.isPending ? "Готовлю…" : "Скачать xlsx"}
    </Button>
  );
}

const muted = "text-sm text-muted-foreground";

/** Что с оптовой частью: ошибка — красным (МП показан), скрыт / не показан — почему, ok — свежесть
 *  и что не вошло (до начала себестоимости). */
function OptNote({ data }: { data: MissingList }): React.ReactElement | null {
  const o = data.opt;
  if (o.status === "not_requested") return null;
  if (o.status === "error") {
    return (
      <p role="alert" className="text-sm text-red-600 dark:text-red-400" data-testid="missing-opt-note">
        Опт не загрузился — показаны только МП: {o.message}
      </p>
    );
  }
  if (o.status === "forbidden") {
    return (
      <p className={muted} data-testid="missing-opt-note">
        Опт скрыт: нет права «Аналитика ОПТ».
      </p>
    );
  }
  if (o.status === "not_applicable") {
    return (
      <p className={muted} data-testid="missing-opt-note">
        Опт не показан: кабинет / площадка — фильтр МП.
      </p>
    );
  }
  const b = o.before_cogs_from;
  return (
    <div className={`space-y-0.5 ${muted}`} data-testid="missing-opt-note">
      {o.state && (
        <p>
          Опт: отгрузки 1С по {dateRu(o.state.sales_date_to)}, остаток заказов на {dateRu(o.state.orders_as_of)}.
        </p>
      )}
      {b && b.rows > 0 && (
        <p>
          Отгрузки опта до начала себестоимости ({dateRu(data.cogs_from)}) без цены не показаны — цен до этой даты нет:{" "}
          {int(b.rows)} стр., {qtyText(b.qty)} шт, {money(b.revenue)}.
        </p>
      )}
    </div>
  );
}

const OPT_KIND_LABEL = { shipped: "отгружено", planned: "к отгрузке", overdue: "просрочено" } as const;

function optBucketText(b: MissingOptBucket): string {
  const rev =
    b.revenue === null
      ? "сумма неизвестна"
      : b.revenue_unknown_rows > 0
        ? `${money(b.revenue)} + ${int(b.revenue_unknown_rows)} стр. без суммы`
        : money(b.revenue);
  return `${qtyText(b.qty)} шт · ${rev}`;
}

function MissingTr({ it, onAdd }: { it: MissingItem; onAdd: () => void }): React.ReactElement {
  const st = MISSING_STATUS_STYLE[it.status];
  const hasRefs = it.nomenclature_ref !== null && it.characteristic_ref !== null;
  const opt = it.by_source.opt;
  const mpPart = it.by_source.mp;
  return (
    <tr className="border-t align-top" data-testid="missing-row">
      <td className="px-2 py-1.5">
        <Badge cls={st.cls}>{st.label}</Badge>
      </td>
      <td className="px-2 py-1.5" data-testid="missing-sources">
        <div className="flex flex-wrap gap-1">
          {it.sources.map((s) => (
            <Badge key={s} cls="bg-muted text-foreground">
              {s === "mp" ? "МП" : "Опт"}
            </Badge>
          ))}
        </div>
      </td>
      <td className="px-2 py-1.5">
        <div className="font-medium">{it.article_1c ?? "—"}</div>
        <div className="text-xs text-muted-foreground">
          {it.name_1c ?? ""}
          {it.code_1c && ` · код ${it.code_1c}`}
        </div>
        {it.characteristic_ref !== null && (
          <CharacteristicLine characteristicRef={it.characteristic_ref} name={it.characteristic_name} />
        )}
      </td>
      <td className="px-2 py-1.5 text-xs">
        {it.offers.map((o, oi) => (
          <div key={`${o.mp}:${o.mp_sku}:${o.vendor_code ?? ""}:${o.name ?? ""}:${oi}`}>
            <span className="uppercase">{o.mp}</span> {o.mp_sku}
            {o.vendor_code && ` · ${o.vendor_code}`}
            {o.name && <span className="text-muted-foreground"> · {o.name}</span>}
          </div>
        ))}
        {opt && (
          <div className="mt-0.5 space-y-0.5" data-testid="missing-opt">
            {(["shipped", "planned", "overdue"] as const).map((k) => {
              const b = opt[k];
              return b ? (
                <div key={k}>
                  <span className="font-medium">Опт, {OPT_KIND_LABEL[k]}:</span> {optBucketText(b)}
                </div>
              ) : null;
            })}
            {it.planned_next && (
              <div className="text-amber-700 dark:text-amber-400" data-testid="missing-planned-comment">
                {it.planned_next.comment}
              </div>
            )}
            {mpPart && <div className="text-muted-foreground">МП: {int(mpPart.qty)} шт · {money(mpPart.revenue)}</div>}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums">{qtyText(it.qty)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{money(it.revenue)}</td>
      <td className="px-2 py-1.5 text-right text-xs">
        {it.status === "no_price" ? (
          it.portal_price ? (
            <span className="text-muted-foreground" data-testid="missing-portal-price">
              цена внесена в портале (с {dateRu(it.portal_price.valid_from)}) — DWH учтёт после
              загрузки
            </span>
          ) : hasRefs ? (
            <Button size="sm" variant="outline" className="h-7" onClick={onAdd}>
              Добавить цену
            </Button>
          ) : (
            <span className="text-red-700 dark:text-red-400">нет ссылок 1С в ответе — добавить нельзя</span>
          )
        ) : (
          <span className="text-muted-foreground">{MISSING_HINT[it.status]}</span>
        )}
      </td>
    </tr>
  );
}
